import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../../axios/axiosInstance';
import { SkillType } from 'model/response';
import React, { useEffect, useRef, useState } from 'react';
import { Input, message, Popconfirm, Select, Table } from 'antd';
import { useMasterGradesSelector, useMasterSubjectsSelector } from '../../../../../redux/selector';
import { ColumnsType } from 'antd/es/table/interface';
import { ModePage } from 'common/enum';
import AddNewSkillType from './AddNewExerciseFormatModal';
import processError from '../../../../../utils/processError';
import { mapToSelectItems } from '../../../../../utils/utils';
import { findAllSkillType, toggleSkillTypeWebviewActive } from '../../../../../service/skillTypeService';
import { setSkillTypes } from '../../../../../redux/slice/masterDataSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../../redux/store/store';
import WebViewActiveCheckbox from '../../../../common/WebViewActiveCheckbox';
import useTableConfig from '../../../../../hooks/useTableConfig';

export default function SkillTypeManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const gradeList = useMasterGradesSelector();
  const subjects = useMasterSubjectsSelector();
  const [keyword, setKeyword] = useState<string>('');
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [skillTypeId, setSkillTypeId] = useState<string>('');
  const [subjectId, setSubjectId] = useState('');
  const [mode, setMode] = useState<ModePage>(ModePage.ADD);
  const rootElement: any = useRef(null);
  const [isOpen, setOpenModal] = useState<boolean>(false);

  const [data, setData] = useState<SkillType[]>();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const columns: ColumnsType<SkillType> = [
    {
      title: 'Dạng năng lực',
      dataIndex: 'name',
      width: '40%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Lớp',
      dataIndex: 'grade',
      render: (_, record) => (record.grade ? record.grade.name : ''),
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('grade.name'),
    },
    {
      title: 'Môn học',
      dataIndex: 'subject',
      render: (_, record) => (record.subject ? record.subject.name : ''),
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('subject.name'),
    },
    {
      title: 'Số câu hỏi',
      dataIndex: 'questionCount',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('questionCount'),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, record) => (
        <WebViewActiveCheckbox
          currentActiveValue={record.webViewActive}
          itemName={'năng lực'}
          setLoading={setLoading}
          toggleWebviewActive={async () => toggleSkillTypeWebviewActive(record.id)}
          reloadPageData={loadListSkillTypes}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter('webViewActive'),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('webViewOrder'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa năng lực"
            description="Xác nhận xóa năng lực. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteSkillType(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteSkillType = async (record: SkillType) => {
    setLoading(true);

    try {
      await axiosInstance({
        url: `/skill-types/${record.id}`,
        method: 'delete',
      });
      message.success('Xoá thành công.');
      await loadListSkillTypes();
    } catch (e) {
      processError(e);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: SkillType) => {
    setMode(ModePage.EDIT);
    setOpenModal(true);
    setSkillTypeId(record.id);
  };

  const loadListSkillTypes = async function () {
    setLoading(true);
    const res = await axiosInstance({
      url: '/skill-types/page',
      params: {
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
        subjectId,
        gradeId: gradeFilter,
        fromCMS: true,
      },
    });

    if (res.status === 200) {
      setData(res.data.content);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onChangeSelect = function (e: any, fieldName: string) {
    switch (fieldName) {
      case 'schoolYear':
        setGradeFilter(e);
        break;
      default:
        break;
    }
  };

  const onSearch = function (e: any) {
    setKeyword(e.target.value);
  };

  const addNewExerciseFormat = async () => {
    setMode(ModePage.ADD);
    setOpenModal(true);
  };

  const onCloseModal = () => {
    setOpenModal(false);
  };

  const handleBeforeSuccess = () => {
    setOpenModal(false);
    loadListSkillTypes().then();
    findAllSkillType()
      .then((skillType) => {
        dispatch(setSkillTypes(skillType));
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    loadListSkillTypes().then();
  }, [currentPage, pageSize, sortBy, direction, gradeFilter, subjectId, keyword]);

  return (
    <div className="w-full h-full p-4" ref={rootElement}>
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewExerciseFormat()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2">
            <div>Năm</div>
            <Select
              onChange={(e) => onChangeSelect(e, 'schoolYear')}
              className="w-full"
              placeholder="Bộ lọc-Năm học"
              style={{ flex: 1 }}
              value={gradeFilter}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(
                gradeList.map((grade) => ({
                  value: grade.id,
                  label: grade.name,
                })),
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <div>Môn học</div>
            <Select
              onChange={(e) => setSubjectId(e)}
              className="w-full min-w-[240px]"
              placeholder="Bộ lọc - Môn học"
              style={{ flex: 1 }}
              value={subjectId}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(mapToSelectItems(subjects))}
            />
          </div>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={(e) => onSearch(e)} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          scroll={{ y: (window.innerHeight * 52) / 100 }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <AddNewSkillType
        isOpen={isOpen}
        onCloseModal={onCloseModal}
        modePage={mode}
        onBeforeSuccess={handleBeforeSuccess}
        skillTypeId={skillTypeId}
      />
    </div>
  );
}
